import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Search, Trash2, type LucideIcon } from 'lucide-react'
import type { CaseEndpoint } from '../../types/case-container'
import { getDeviceIcon } from './endpoint-icons'

export interface EndpointSetCopy {
  titleKey: string
  endpointCountKey: string
  noSearchResultsKey: string
  noEndpointsYetKey: string
  addHintKey: string
  selectAllKey: string
  showingCountKey: string
  filteredKey: string
  clearAllConfirmKey: string
  yesClearKey: string
  clearAllKey: string
  removeTooltipKey: string
}

export interface EndpointSetVisuals {
  icon: LucideIcon
  iconBgClass: string
  iconColorClass: string
}

interface HeaderProps {
  visuals: EndpointSetVisuals
  copy: EndpointSetCopy
  count: number
  onClose: () => void
}

export function EndpointSetHeader({ visuals, copy, count, onClose }: HeaderProps): ReactElement {
  const { t } = useTranslation('cases')
  const Icon = visuals.icon
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
      <div className="flex items-center gap-3">
        <div
          className={`flex h-10 w-10 items-center justify-center rounded-lg ${visuals.iconBgClass}`}
        >
          <Icon className={`h-5 w-5 ${visuals.iconColorClass}`} />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t(copy.titleKey as never)}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t(copy.endpointCountKey as never, { count })}
          </p>
        </div>
      </div>
      <button
        onClick={onClose}
        className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface SelectionToolbarProps {
  selectedCount: number
  onDeselectAll: () => void
  onRemoveSelected: () => void
}

function SelectionToolbar({
  selectedCount,
  onDeselectAll,
  onRemoveSelected,
}: SelectionToolbarProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-500 dark:text-slate-400">
        {t('infrastructure.selectedCount', { count: selectedCount })}
      </span>
      <button
        onClick={onDeselectAll}
        className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
      >
        {t('actions.clear')}
      </button>
      <button
        onClick={onRemoveSelected}
        className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-1 text-sm text-red-600 hover:bg-red-100 dark:bg-red-950/50 dark:text-red-400 dark:hover:bg-red-950"
      >
        <Trash2 className="h-3.5 w-3.5" />
        {t('actions.remove')}
      </button>
    </div>
  )
}

interface SearchBarProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  selectedCount: number
  hasFilteredItems: boolean
  onSelectAll: () => void
  onDeselectAll: () => void
  onRemoveSelected: () => void
  selectAllKey: string
}

export function EndpointSetSearchBar({
  searchTerm,
  onSearchChange,
  selectedCount,
  hasFilteredItems,
  onSelectAll,
  onDeselectAll,
  onRemoveSelected,
  selectAllKey,
}: SearchBarProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-center gap-3 border-b border-slate-200 px-6 py-3 dark:border-slate-700">
      <div className="relative flex-1">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value)
          }}
          placeholder={t('infrastructure.searchEndpoints')}
          className="w-full rounded-md border border-slate-300 bg-white py-2 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        />
      </div>
      {selectedCount > 0 ? (
        <SelectionToolbar
          selectedCount={selectedCount}
          onDeselectAll={onDeselectAll}
          onRemoveSelected={onRemoveSelected}
        />
      ) : (
        hasFilteredItems && (
          <button
            onClick={onSelectAll}
            className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          >
            {t(selectAllKey as never)}
          </button>
        )
      )}
    </div>
  )
}

interface EndpointRowProps {
  endpoint: CaseEndpoint
  isSelected: boolean
  onToggle: (id: string) => void
  onRemove: (id: string) => void
  removeTooltipKey: string
}

export function EndpointRow({
  endpoint,
  isSelected,
  onToggle,
  onRemove,
  removeTooltipKey,
}: EndpointRowProps): ReactElement {
  const { t } = useTranslation('cases')
  const containerClass = `flex items-center gap-3 p-3 rounded-lg border transition-colors ${
    isSelected
      ? 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-950/30'
      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
  }`
  const nameClass = `font-medium truncate ${
    isSelected ? 'text-fuchsia-700 dark:text-fuchsia-400' : 'text-slate-900 dark:text-slate-100'
  }`
  return (
    <div className={containerClass}>
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => {
          onToggle(endpoint.id)
        }}
        className="h-4 w-4 rounded border-slate-300 text-fuchsia-600 focus:ring-fuchsia-500 dark:border-slate-600"
      />
      {getDeviceIcon(endpoint.deviceType)}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className={nameClass}>{endpoint.hostname}</span>
          {endpoint.deviceType && (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
              {endpoint.deviceType.replaceAll('_', ' ')}
            </span>
          )}
        </div>
        {endpoint.ipAddresses && endpoint.ipAddresses.length > 0 && (
          <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
            {endpoint.ipAddresses.join(', ')}
          </p>
        )}
      </div>
      <button
        onClick={() => {
          onRemove(endpoint.id)
        }}
        className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/50 dark:hover:text-red-400"
        title={t(removeTooltipKey as never)}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}

interface EmptyStateProps {
  visuals: EndpointSetVisuals
  copy: EndpointSetCopy
  searchTerm: string
}

export function EndpointSetEmpty({ visuals, copy, searchTerm }: EmptyStateProps): ReactElement {
  const { t } = useTranslation('cases')
  const Icon = visuals.icon
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Icon className="mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
      <p className="text-slate-600 dark:text-slate-400">
        {searchTerm ? t(copy.noSearchResultsKey as never) : t(copy.noEndpointsYetKey as never)}
      </p>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-500">
        {!searchTerm && t(copy.addHintKey as never)}
      </p>
    </div>
  )
}

interface ClearConfirmProps {
  copy: EndpointSetCopy
  onConfirm: () => void
  onCancel: () => void
}

function ClearConfirm({ copy, onConfirm, onCancel }: ClearConfirmProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-slate-600 dark:text-slate-400">
        {t(copy.clearAllConfirmKey as never)}
      </span>
      <button
        onClick={onConfirm}
        className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
      >
        {t(copy.yesClearKey as never)}
      </button>
      <button
        onClick={onCancel}
        className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.cancel')}
      </button>
    </div>
  )
}

interface FooterActionsProps {
  copy: EndpointSetCopy
  totalCount: number
  showClearConfirm: boolean
  onShowClearConfirm: () => void
  onConfirmClear: () => void
  onCancelClear: () => void
  onClose: () => void
}

function FooterActions({
  copy,
  totalCount,
  showClearConfirm,
  onShowClearConfirm,
  onConfirmClear,
  onCancelClear,
  onClose,
}: FooterActionsProps): ReactElement {
  const { t } = useTranslation('cases')
  if (showClearConfirm) {
    return <ClearConfirm copy={copy} onConfirm={onConfirmClear} onCancel={onCancelClear} />
  }
  return (
    <>
      {totalCount > 0 && (
        <button
          onClick={onShowClearConfirm}
          className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/50"
        >
          <Trash2 className="h-4 w-4" />
          {t(copy.clearAllKey as never)}
        </button>
      )}
      <button
        onClick={onClose}
        className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('actions.close')}
      </button>
    </>
  )
}

export interface EndpointSetFooterProps {
  copy: EndpointSetCopy
  filteredCount: number
  totalCount: number
  searchTerm: string
  showClearConfirm: boolean
  onShowClearConfirm: () => void
  onConfirmClear: () => void
  onCancelClear: () => void
  onClose: () => void
}

export function EndpointSetFooter({
  copy,
  filteredCount,
  totalCount,
  searchTerm,
  showClearConfirm,
  onShowClearConfirm,
  onConfirmClear,
  onCancelClear,
  onClose,
}: EndpointSetFooterProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
      <div className="text-sm text-slate-500 dark:text-slate-400">
        {t(copy.showingCountKey as never, { filtered: filteredCount, total: totalCount })}
        {searchTerm && ` (${String(t(copy.filteredKey as never))})`}
      </div>
      <div className="flex items-center gap-3">
        <FooterActions
          copy={copy}
          totalCount={totalCount}
          showClearConfirm={showClearConfirm}
          onShowClearConfirm={onShowClearConfirm}
          onConfirmClear={onConfirmClear}
          onCancelClear={onCancelClear}
          onClose={onClose}
        />
      </div>
    </div>
  )
}
